/**
 * Deployment API service.
 */
import { api } from './api'
import type {
  DeploymentRequest,
  CreateDeploymentRequest,
  DeploymentLog,
  DeploymentStage,
  PaginatedResponse,
  DeploymentFilters,
  PipelineRun,
  AppriseVmDetail,
} from '@/types'

export interface ErpVersion {
  id: number
  name: string
  display_name: string
  major_version: string
  is_active: boolean
  created_at: string
}

export const deploymentsApi = {
  list: (filters?: DeploymentFilters) =>
    api.get<PaginatedResponse<DeploymentRequest>>('/deployments/requests/', {
      params: filters,
    }),

  get: (id: number) =>
    api.get<DeploymentRequest>(`/deployments/requests/${id}/`),

  create: (data: CreateDeploymentRequest) =>
    api.post<DeploymentRequest>('/deployments/requests/', data),

  trigger: (id: number) =>
    api.post<DeploymentRequest>(`/deployments/requests/${id}/trigger/`),

  cancel: (id: number) =>
    api.post<DeploymentRequest>(`/deployments/requests/${id}/cancel/`),

  getLogs: (id: number) =>
    api.get<DeploymentLog[]>(`/deployments/requests/${id}/logs/`),

  getStages: (id: number) =>
    api.get<DeploymentStage[]>(`/deployments/requests/${id}/stages/`),

  getPipelineRun: (id: number) =>
    api.get<PipelineRun>(`/deployments/requests/${id}/pipeline-run/`),

  getAppPipelineRun: (id: number) =>
    api.get<PipelineRun>(`/deployments/requests/${id}/app-pipeline-run/`),

  switchTraffic: (id: number, notes?: string) =>
    api.post(`/deployments/requests/${id}/switch_traffic/`, { notes }),

  triggerAutoFix: (id: number, stageId: number) =>
    api.post(`/deployments/requests/${id}/trigger_autofix/`, { stage_id: stageId }),

  // Apprise-on-VM: resolved config, plan history, server outputs, verify + tracks, and plan approval.
  getAppriseVm: (id: number) =>
    api.get<AppriseVmDetail>(`/deployments/requests/${id}/apprise-vm/`),

  approvePlan: (id: number, notes: string) =>
    api.post<DeploymentRequest>(`/deployments/requests/${id}/approve-plan/`, { notes }),

  rejectPlan: (id: number, notes: string) =>
    api.post<DeploymentRequest>(`/deployments/requests/${id}/reject-plan/`, { notes }),

  getErpVersions: () =>
    api.get<ErpVersion[]>('/deployments/erp-versions/'),

  createErpVersion: (data: { name: string; display_name: string; major_version: string }) =>
    api.post<ErpVersion>('/deployments/erp-versions/', data),

  deleteErpVersion: (id: number) =>
    api.delete(`/deployments/erp-versions/${id}/`),

  getBacpacFiles: (refresh = false, environment = '') =>
    api.get<{ files: BacpacFile[]; cached: boolean; stale?: boolean; error?: string; account?: string; container?: string; source?: 'prod' | 'test' }>(
      '/products/bacpac-files/',
      { params: { ...(refresh ? { refresh: 'true' } : {}), ...(environment ? { environment } : {}) } }
    ),
}

export interface BacpacFile {
  name: string
  size_mb: number
  last_modified: string
}
